// getting construction features options from OSS type definitions
// https://www.optaservice.com/optasingleservice/test?json=simplifiedschema.json

import storage from "./localstorage";
import config from "../config";
import { isObject, isString, isArray, getProp } from "./util";
import { getHttpClient } from "./api";
import { filterFeatureOptions} from "../languages/schema-translations";

import getLogger from "./debug-logger";
import { getGlobalValue, setGlobalValue } from "./global-util";

const log = getLogger("oss-schema", 2);

const { ossSchemaUrl, ossApiTimeout } = config;

// enumerations not defined in OSS schema dataset
const missingTypes = {
  "xsd:boolean": ["true", "false"],
};

// extract values for OSS schema enumeration types
const getOptions = (ossTypesObj, dataKey) => {
  const x = getProp(ossTypesObj, dataKey) || getProp(ossTypesObj, `${dataKey}Type`);
  if (isArray(x)) return filterFeatureOptions(x); // final array of values (filter off Fr options if feat has Fr values in schema)
  if (isString(x)) return getOptions(ossTypesObj, x); // dataKey is reference to another key
  if (isObject(x)) {
    const o = Object.create(null);
    for (let [k, v] of Object.entries(x)) {
      let y = getOptions(ossTypesObj, v);
      if (isArray(y)) o[k] = [...y];
    }
    return o;
  }
  return null;
};

const fetchOssSchema = async () => {
  const opt = {
    method: "GET",
    url: ossSchemaUrl,
    timeout: ossApiTimeout,
    validateStatus: null,
  };
  const client = getHttpClient();
  log(`sending request for OSS schema`, opt); //---------------- log
  try {
    const res = await client.request(opt);
    let schema = getProp(res, "data");
    if (isObject(schema)) {
      log(`OSS schema received`, schema); //---------------- log
      // add missing enumerations
      schema = Object.assign({}, missingTypes, schema);
      return schema;
    }
    log(`no data returned`); //-------------------------- LOG
  } catch (x) {
    const msg = (typeof x === "object" && x.message) || x + "";
    log(msg); //---------------- log
  }
  log(`Schema API call failed`); //---------------- log
  return null;
};

// pull or refresh the data schema
export const loadOssSchema = async () => {
  let schema = getGlobalValue("DATA_SCHEMA");
  if (schema) {
    return;
  }
  storage.remove("__datasSchema"); // for migration from old key - to be removed after a while
  const storageKey = "__dataschema";
  schema = storage.get(storageKey);
  if (schema) {
    log("stored data schema found", schema); //--------------- log
    setGlobalValue("DATA_SCHEMA", schema);
    return;
  }
  log(`loading OSS schema from API`); //---------------- log
  const rawSchema = await fetchOssSchema();
  if (isObject(rawSchema)) {
    schema = transformOssSchema(rawSchema);
    log(`storing transformed schema downloaded from API`); //--------------- log
    storage.set(storageKey, schema);
    log(`Check storage key '${storageKey}' in DevTools 'Application' tab for stored schema`); //--------------- log
    setGlobalValue("DATA_SCHEMA", schema);
  }
}

// transform raw OSS schema into a more convenient shape for our use, and blend in property stats features
const transformOssSchema = (schema) => {
  // handling property stats - both top level entries and a group
  const propertyStats = {};
  ["PropertyOccupancy", "ZoningDescription", "Tenure", "RentalActivity"].forEach((k) => {
    let kk = k + "Type";
    if (kk in schema) {
      schema[k] = schema[kk];
      delete schema[kk];
      propertyStats[k] = schema[k];
    }
  });
  const transformed = {
    residential: getOptions(schema, "ResponseFinancialResidentialConstructionFeaturesType"),
    condoBuilding: getOptions(schema, "ResponseCondoBuildingFeaturesType"),
    condoUnit: getOptions(schema, "ResponseCondoUnitFeaturesType"),
    rentalTypeValues: getOptions(schema, "RentalUnitType"),

    propertyStats, // grouped together
  };

  // blend in property stats for residential
  Object.assign(transformed.residential, propertyStats);

  return transformed;
};

// provide construction features' options from local storage or API
export const getFeatsOptions = (dataSchema, propType = "") => {
  if (!dataSchema) {
    return null;
  }
  const featsMap =
    propType === "Condominium"
      ? { ...dataSchema.condoBuilding, ...dataSchema.condoUnit }
      : propType === "Residential"
        ? { ...dataSchema.residential }
        : { ...dataSchema.residential, ...dataSchema.condoBuilding, ...dataSchema.condoUnit };
  return featsMap;
};
